import { Link } from 'react-router-dom';
import type { Comic } from '@/mocks/comics';

interface ComicCardProps {
  comic: Comic;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ComicCard({ comic, variant = 'default' }: ComicCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/comic/${comic.id}`}
        className="group block bg-background-50 rounded-lg overflow-hidden transition-all duration-300 hover:-translate-y-1"
      >
        <div className="relative w-full aspect-[3/4] overflow-hidden">
          <img
            src={comic.coverImage}
            alt={comic.title}
            title={`${comic.title} - 韩漫画网韩漫在线阅读`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute top-2 right-2">
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${comic.status === '连载中' ? 'bg-primary-500 text-background-50' : 'bg-secondary-500 text-background-50'}`}>
              {comic.status}
            </span>
          </div>
        </div>
        <div className="p-3">
          <h3 className="text-sm font-semibold text-foreground-900 truncate font-heading">{comic.title}</h3>
          <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/comic/${comic.id}`}
        className="group flex gap-4 bg-background-50 rounded-lg p-4 transition-all duration-300 hover:-translate-y-1 min-w-[320px]"
      >
        <div className="relative w-24 h-32 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={comic.coverImage}
            alt={comic.title}
            title={`${comic.title} - 韩漫画网`}
            className="w-full h-full object-cover object-top"
            loading="lazy"
          />
          <div className="absolute top-1.5 right-1.5">
            <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium whitespace-nowrap ${comic.status === '连载中' ? 'bg-primary-500 text-background-50' : 'bg-secondary-500 text-background-50'}`}>
              {comic.status}
            </span>
          </div>
        </div>
        <div className="flex flex-col justify-between flex-1 min-w-0">
          <div>
            <h3 className="text-base font-semibold text-foreground-900 font-heading">{comic.title}</h3>
            <p className="text-xs text-foreground-500 mt-1">{comic.author} · {comic.category}</p>
            <p className="text-xs text-foreground-600 mt-2 line-clamp-2 leading-relaxed">{comic.description}</p>
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-primary-500 font-medium">{comic.latestChapter}</span>
            <span className="flex items-center gap-1 text-xs text-foreground-400">
              <i className="ri-eye-line text-[10px]"></i>
              {comic.views}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/comic/${comic.id}`}
      className="group block bg-background-50 rounded-lg overflow-hidden transition-all duration-300 hover:-translate-y-1.5"
    >
      <div className="relative w-full aspect-[3/4] overflow-hidden">
        <img
          src={comic.coverImage}
          alt={comic.title}
          title={`${comic.title} - 韩漫画网韩漫免费在线阅读`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        <div className="absolute top-2.5 right-2.5">
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${comic.status === '连载中' ? 'bg-primary-500 text-background-50' : 'bg-secondary-500 text-background-50'}`}>
            {comic.status}
          </span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
          <span className="text-xs text-background-50 bg-primary-500/90 px-2 py-1 rounded-md inline-block">
            {comic.latestChapter}
          </span>
        </div>
      </div>
      <div className="p-3">
        <h3 className="text-sm font-semibold text-foreground-900 truncate font-heading">{comic.title}</h3>
        <div className="flex items-center justify-between mt-1.5">
          <p className="text-xs text-foreground-500">{comic.author}</p>
          <span className="flex items-center gap-0.5 text-xs text-foreground-400">
            <i className="ri-star-fill text-[10px] text-amber-400"></i>
            {comic.rating}
          </span>
        </div>
      </div>
    </Link>
  );
}